import dayjs, {type Dayjs} from 'utils/dayjs';

/** @module utils/date-time */

/**
 * Timeformat for the api in 24 hour format
 * @example 'HH:mm' => '23:00'
 */
export const apiTimeFormat = 'HH:mm';

/**
 * Dateformat for the api
 * @example 'YYYY-MM-DD' => '2020-01-01'
 */
export const apiDateFormat = 'YYYY-MM-DD';

export const apiDateTimeFormat = `${apiDateFormat}T${apiTimeFormat}`;

/**
 *  Timeformat for the display in 12 hour format
 * @example 'hh:mm A' => '11:00 PM'
 */
export const displayTimeMeridiem = 'hh:mm A';

/**
 * Date format display as 2 digit month and day and 4 digit year
 * @example 'MM/DD/YYYY' => '01/01/2020'
 */
export const displayDateFormat = 'MM/DD/YYYY';

/**
 * Date format display as 2-digit month and day and 2-digit year
 * @example 'MM/DD/YYYY' => '01/01/20'
 */
export const shortDisplayDateFormat = 'MM/DD/YY';

/**
 * Time format display in 24 hour format with seconds
 * @example 'HH:mm:ss' => '23:00:00'
 */
export const display24HourFormat = 'HH:mm:ss';

/**
 * Date and Time format display in 12 hour format with seconds
 * @example 'ddd, MMM D, YYYY hh:mm:ss A' => 'Mon, Jan 1, 2020 11:00:00 PM'
 */
export const displayDateTimeMeridiem = `ddd, MMM D, YYYY ${displayTimeMeridiem}`;

/**
 * Date format display as Month and Year
 * @example 'MMMM YYYY' => 'January 2020'
 */
export const fullMonthYearFormat = 'MMMM YYYY';

/**
 * Day names for Monday through Friday.
 */
export const WEEKDAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
];

/**
 * Day names for Saturday and Sunday.
 */
export const WEEKENDS = ['Saturday', 'Sunday'];

/**
 * Labels for relative days.
 */
export const RELATIVE_DAYS = {
    TODAY: 'Today',
};

/**
 * Day names for the full week, starting Monday.
 */
export const DAYS_OF_WEEK = [...WEEKDAYS, ...WEEKENDS];

/**
 * Map of day name (lowercase) to its three-letter abbreviation, keyed by lowercase day name.
 * @example DAYS_WITH_LABELS.monday.label === 'Mon'
 */
export const DAYS_WITH_LABELS = DAYS_OF_WEEK.reduce<
    Record<string, {label: string}>
>((acc, day) => {
    acc[day.toLowerCase()] = {
        label: day.substring(0, 3),
    };

    return acc;
}, {});

/**
 * Rounds a given date to the next half-hour interval.
 */
export const ceilToNextHalfHour = (date: string | Dayjs) => {
    const time = dayjs(date);
    const roundedToMinute = time.startOf('minute');
    const hasSubMinute = !time.isSame(roundedToMinute);
    const minutes = roundedToMinute.minute();
    const remainder = minutes % 30;
    const minutesToAdd =
        remainder === 0 ? (hasSubMinute ? 30 : 0) : 30 - remainder;

    return roundedToMinute.add(minutesToAdd, 'minute');
};
